// Achat intégré Apple / Google, via RevenueCat (version « monétisation », 09/2026).
//
// Qui décide de quoi :
// - L'app ne fait que lancer l'achat chez Apple ou Google. Elle ne décrète jamais
//   qu'une personne est Premium.
// - Juste après un achat ou une restauration, elle demande au serveur de relire le
//   client chez RevenueCat (POST /api/billing/store/entitlements/). Le serveur est
//   la seule source de vérité du mur des scans et de la boutique.
// - Les prix affichés viennent du store, jamais d'un texte écrit en dur : c'est ce
//   qui rend impossible de reproduire le rejet Apple 3.1.1 de septembre 2026.
//
// Le JavaScript de l'app part aussi, par mise à jour à distance, vers des binaires
// plus anciens qui n'embarquent pas le module de paiement : tout passe donc par
// isStoreBillingAvailable(), qui vérifie que le module est bien là.

import type { PurchasesPackage } from '@revenuecat/purchases-capacitor';

import { apiFetch } from '@/lib/api/client';
import { nativePlatform } from '@/lib/native';
import { openExternalUrl } from '@/lib/open-external';

// Clés PUBLIQUES RevenueCat : conçues pour être embarquées dans l'app, elles ne
// permettent que de lancer un achat. Android : app « Liberio Android » créée dans
// RevenueCat le 18/09/2026, mêmes produits rangés dans l'offre `default`.
const REVENUECAT_PUBLIC_KEYS = {
  ios: process.env.NEXT_PUBLIC_REVENUECAT_IOS_KEY || 'appl_XZvxXAjDcAIahpDDGnAgfObVXDf',
  android: process.env.NEXT_PUBLIC_REVENUECAT_ANDROID_KEY || 'goog_VKosgIgAZeuMgiOjVwlwDtMhIZv',
};

// Identifiants des « packages » de l'offre `default` dans RevenueCat.
const PACKAGE_IDS = { monthly: '$rc_monthly', annual: '$rc_annual', pack: 'pack' } as const;

const MANAGE_SUBSCRIPTIONS_URLS = {
  ios: 'https://apps.apple.com/account/subscriptions',
  android: 'https://play.google.com/store/account/subscriptions',
};

export type StorePlan = keyof typeof PACKAGE_IDS;

export type StoreOffer = {
  plan: StorePlan;
  // Prix formaté par le store, dans la devise du compte (« 24,99 € »).
  priceString: string;
  price: number;
  currencyCode: string;
  pkg: PurchasesPackage;
};

export type StoreOffers = Record<StorePlan, StoreOffer | null>;

export type StoreEntitlements = {
  app_user_id: string;
  premium_active: boolean;
  event_pack_active: boolean;
  // Abonnement Premium acheté en magasin et en cours (ce qu'on peut « gérer ou
  // résilier » dans les réglages du store ; un Pack seul n'a rien à gérer).
  store_subscription_active: boolean;
  store_premium_expires_at: string | null;
  event_pack_expires_at: string | null;
};

export type StorePurchaseOutcome =
  | { status: 'purchased'; entitlements: StoreEntitlements | null }
  | { status: 'cancelled' }
  | { status: 'pending' };

type CapacitorWithPlugins = { isPluginAvailable?: (name: string) => boolean };

// N'importe quel écran peut demander l'écran d'achat (la boutique gratuite pleine,
// par exemple) : la page principale écoute cet événement et l'ouvre.
export const PREMIUM_WALL_REQUEST_EVENT = 'liberio:premium-wall-request';
export type PremiumWallRequestDetail = { trigger: 'shop_item_limit' };

export function requestPremiumWall(trigger: PremiumWallRequestDetail['trigger']) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent<PremiumWallRequestDetail>(PREMIUM_WALL_REQUEST_EVENT, { detail: { trigger } }));
}

function publicKey(): string {
  const platform = nativePlatform();
  return platform === 'web' ? '' : REVENUECAT_PUBLIC_KEYS[platform];
}

function purchasesPluginInBinary(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const capacitor = (window as Window & { Capacitor?: CapacitorWithPlugins }).Capacitor;
  return Boolean(capacitor?.isPluginAvailable?.('Purchases'));
}

export function isStoreBillingAvailable(): boolean {
  return nativePlatform() !== 'web' && publicKey() !== '' && purchasesPluginInBinary();
}

export function fetchStoreEntitlements() {
  return apiFetch('/api/billing/store/entitlements/', { auth: true }) as Promise<StoreEntitlements>;
}

function syncStoreEntitlements() {
  return apiFetch('/api/billing/store/entitlements/', { method: 'POST', auth: true }) as Promise<StoreEntitlements>;
}

let configuredAppUserId: string | null = null;

type PurchasesModule = typeof import('@revenuecat/purchases-capacitor');

// Le client RevenueCat est toujours celui du compte connecté (id de son acteur,
// fourni par le serveur) : un achat ne peut ainsi jamais atterrir sur un
// identifiant anonyme, ni sur le compte d'avant une déconnexion.
//
// On renvoie le MODULE, jamais le plugin lui-même : un plugin Capacitor répond à
// n'importe quel nom de méthode, `then` compris. Renvoyé par une fonction async,
// il passe pour une promesse, JavaScript appelle son `then` natif (inexistant)
// et l'attente ne se termine jamais (constaté au simulateur le 18/09/2026).
//
// Appels mis en file : la préparation au lancement et un geste de la personne
// (restaurer, acheter) ne doivent jamais configurer le module deux fois en
// parallèle.
let accountQueue: Promise<unknown> = Promise.resolve();

function purchasesForCurrentAccount(): Promise<PurchasesModule> {
  const next = accountQueue.then(setUpPurchasesForCurrentAccount);
  accountQueue = next.catch(() => undefined);
  return next;
}

async function setUpPurchasesForCurrentAccount(): Promise<PurchasesModule> {
  const purchasesModule = await import('@revenuecat/purchases-capacitor');
  const { Purchases } = purchasesModule;
  const { app_user_id: appUserId } = await fetchStoreEntitlements();
  const { isConfigured } = await Purchases.isConfigured();
  if (!isConfigured) {
    await Purchases.configure({ apiKey: publicKey(), appUserID: appUserId });
  } else if (configuredAppUserId !== appUserId) {
    await Purchases.logIn({ appUserID: appUserId });
  }
  configuredAppUserId = appUserId;
  return purchasesModule;
}

// Prix prêts d'avance (19/09/2026) : la première demande de prix à Apple prend
// plusieurs secondes (bien plus en TestFlight), et l'écran d'achat attendait
// qu'on l'ouvre pour la lancer. On démarre le module et on demande l'offre dès
// l'ouverture de l'app, pour un compte connecté : RevenueCat garde l'offre en
// mémoire et la tient à jour, l'écran d'achat la reçoit alors aussitôt. C'est
// aussi le moment que RevenueCat recommande pour démarrer son module. Une fois
// par lancement ; un échec ici ne se voit pas, l'écran d'achat réessaiera.
let warmUpStarted = false;

export function warmUpStoreOffers() {
  if (warmUpStarted || !isStoreBillingAvailable()) {
    return;
  }
  warmUpStarted = true;
  purchasesForCurrentAccount()
    .then(({ Purchases }) => Purchases.getOfferings())
    .catch(() => {
      warmUpStarted = false;
    });
}

function toOffer(plan: StorePlan, pkg: PurchasesPackage | null | undefined): StoreOffer | null {
  if (!pkg) {
    return null;
  }
  return {
    plan,
    priceString: pkg.product.priceString,
    price: pkg.product.price,
    currencyCode: pkg.product.currencyCode,
    pkg,
  };
}

// null = rien à vendre (offre introuvable ou store injoignable) : l'écran d'achat
// affiche alors un état d'erreur plutôt que des colonnes sans prix.
export async function loadStoreOffers(): Promise<StoreOffers | null> {
  const { Purchases } = await purchasesForCurrentAccount();
  const { current } = await Purchases.getOfferings();
  if (!current) {
    return null;
  }
  // Casse ignorée : le package du Pack s'appelle `Pack` dans RevenueCat (18/09).
  const byId = (identifier: string) =>
    current.availablePackages.find((pkg) => pkg.identifier.toLowerCase() === identifier.toLowerCase());
  const offers: StoreOffers = {
    monthly: toOffer('monthly', current.monthly ?? byId(PACKAGE_IDS.monthly)),
    annual: toOffer('annual', current.annual ?? byId(PACKAGE_IDS.annual)),
    pack: toOffer('pack', byId(PACKAGE_IDS.pack)),
  };
  return offers.monthly || offers.annual || offers.pack ? offers : null;
}

function purchaseErrorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: unknown }).code);
  }
  return '';
}

// Codes RevenueCat (PURCHASES_ERROR_CODE) : 1 = annulé par la personne,
// 20 = paiement en attente (validation parentale, par exemple).
const PURCHASE_CANCELLED = '1';
const PAYMENT_PENDING = '20';

export async function purchaseStoreOffer(offer: StoreOffer): Promise<StorePurchaseOutcome> {
  const { Purchases } = await purchasesForCurrentAccount();
  try {
    await Purchases.purchasePackage({ aPackage: offer.pkg });
  } catch (error) {
    const code = purchaseErrorCode(error);
    if (code === PURCHASE_CANCELLED) {
      return { status: 'cancelled' };
    }
    if (code === PAYMENT_PENDING) {
      return { status: 'pending' };
    }
    throw error;
  }
  // L'achat est fait chez Apple / Google. Si la relecture échoue ici, la
  // notification RevenueCat débloquera le compte quelques secondes plus tard.
  try {
    return { status: 'purchased', entitlements: await syncStoreEntitlements() };
  } catch {
    return { status: 'purchased', entitlements: null };
  }
}

export async function restoreStorePurchases(): Promise<StoreEntitlements> {
  const { Purchases } = await purchasesForCurrentAccount();
  await Purchases.restorePurchases();
  return syncStoreEntitlements();
}

// Gérer ou résilier : toujours dans les réglages d'abonnement du store, jamais chez
// nous (exigence Apple et Google).
export function openStoreSubscriptionSettings(): boolean {
  const platform = nativePlatform();
  return platform === 'web' ? false : openExternalUrl(MANAGE_SUBSCRIPTIONS_URLS[platform]);
}
